import type { AccountPreferences, Me } from '@ankor-io/common/types/account'

import { http, unwrap, withoutAuth } from '../client'
import { iamBase } from '../utils'

/**
 * IAM service for user and account management.
 * Encapsulates all IAM-related API calls with type safety.
 */
export const iamService = {
  /**
   * Fetches the current authenticated user's account data.
   * Includes organization memberships and user preferences.
   *
   * @returns Promise resolving to Me object with organizations and preferences
   * @throws Error if the API request fails
   *
   * @example
   * ```ts
   * const accountData = await iamService.getMe()
   * console.log(accountData.organizations)
   * ```
   */
  async getMe(): Promise<Me> {
    const url = `${iamBase()}/me`
    const response = await http.get<Me>(url)
    return unwrap(response)
  },

  /**
   * Updates the current user's account preferences.
   * Allows updating individual preference keys or multiple at once.
   *
   * @param preferences - Partial preferences object to update
   * @returns Promise resolving to updated preferences
   * @throws Error if the API request fails
   *
   * @example
   * ```ts
   * await iamService.updatePreferences({
   *   activeOrganizationId: 'org-123',
   *   theme: 'dark'
   * })
   * ```
   */
  async updatePreferences(preferences: AccountPreferences): Promise<AccountPreferences> {
    const url = `${iamBase()}/me/preferences`
    const response = await http.put<AccountPreferences>(url, { preferences })
    return unwrap(response)
  },

  /**
   * Checks if an account exists for the given email address.
   * This is a public endpoint that doesn't require authentication.
   *
   * Backend behavior:
   * - Returns 200 (empty object) if account exists
   * - Returns 404 if account doesn't exist
   *
   * @param email - Email address to check
   * @returns Promise resolving to true if account exists, false otherwise
   * @throws Error if the API request fails
   *
   * @example
   * ```ts
   * const exists = await iamService.checkAccountExists('user@example.com')
   * if (exists) {
   *   // Show sign in flow
   * } else {
   *   // Show sign up flow
   * }
   * ```
   */
  async checkAccountExists(email: string): Promise<boolean> {
    const url = `${iamBase()}/account/check`
    try {
      await http.post(url, { email }, withoutAuth())
      // If request succeeds (200), account exists
      return true
    } catch (error) {
      // If request fails with 404, account doesn't exist
      // Any other error should be re-thrown
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status: number } }
        if (axiosError.response?.status === 404) {
          return false
        }
      }
      // Re-throw non-404 errors
      throw error
    }
  },
}
